import { ApiDimension } from "@incendium/api";
import { friendlyDate, friendlyDateTime, friendlyMonth } from "Helpers/dates";
import { TDimensionMetricMap } from "features/analytics/types/types";

const dimensionMap: { [key in ApiDimension]?: TDimensionMetricMap } = {
  [ApiDimension.DIMENSION_NOT_SET]: {
    name: "not set",
    description: "",
  },
  [ApiDimension.DIMENSION_SESSION_START_BY_DAY]: {
    name: "Session Start by Day",
    description: "start of the day the session happened on",
    format: (v) => friendlyDate(`${v}`),
  },
  [ApiDimension.DIMENSION_LOCATION]: {
    name: "Page Location",
    description: "pageview location",
  },
  [ApiDimension.DIMENSION_BROWSER]: {
    name: "Browser",
    description: "leads browser",
  },
  [ApiDimension.DIMENSION_BROWSER_VERSION]: {
    name: "Browser Version",
    description: "leads browser version",
  },
  [ApiDimension.DIMENSION_BROWSER_WITH_VERSION]: {
    name: "Browser & Version",
    description: "leads browser & version",
  },
  [ApiDimension.DIMENSION_OS]: {
    name: "Operating System",
    description: "",
  },
  [ApiDimension.DIMENSION_DEVICE]: {
    name: "Device",
    description: "is mobile or desktop",
  },
  [ApiDimension.DIMENSION_CHANNEL]: {
    name: "Channel",
    description: "channel that brought in a session",
  },
  [ApiDimension.DIMENSION_SOURCE_BUCKET]: {
    name: "Traffic Bucket",
    description:
      "Grouped together sources etc, differs to channels as a session can be in more than 1 bucket.",
  },
  [ApiDimension.DIMENSION_VISITOR_TYPE]: {
    name: "User Type",
    description: "this is new vs returning leads",
  },
  [ApiDimension.DIMENSION_CUSTOMER_TYPE]: {
    name: "Customer Type",
    description: "this is new vs returning customers",
  },
  [ApiDimension.DIMENSION_COUNTRY]: {
    name: "Country",
    description: "by lead country",
  },
  [ApiDimension.DIMENSION_REGION]: {
    name: "Region",
    description: "by lead region",
  },
  [ApiDimension.DIMENSION_CITY]: {
    name: "City",
    plural: "Cities",
    description: "by lead city",
  },
  [ApiDimension.DIMENSION_LAT_LONG]: {
    name: "Longitude & Latitude",
    description: "by longitude - latitude",
  },
  [ApiDimension.DIMENSION_SESSION_AUDIENCE]: {
    name: "Static Audience Name",
    description: "by the audiences a a session was (static)",
  },
  [ApiDimension.DIMENSION_LEAD_AUDIENCE]: {
    name: "Dynamic Audience Name",
    description: "by the audiences a lead can be (dynamic)",
  },
  [ApiDimension.DIMENSION_SOURCE]: {
    name: "Source",
    description: "source that brought in a session",
  },
  [ApiDimension.DIMENSION_MEDIUM]: {
    name: "Medium",
    description: "medium that brought in a session",
  },
  [ApiDimension.DIMENSION_CAMPAIGN]: {
    name: "Campaigns",
    description: "Campaign that brought in a session",
  },
  [ApiDimension.DIMENSION_INTERNAL_CAMPAIGN]: {
    name: "Personsalisation Campaign",
    description: "Personsalisation Campaign that runs a personalisation",
  },
  [ApiDimension.DIMENSION_PERSONALISATION]: {
    name: "Personsalisation",
    description: "Personsalisation that is part of a campaign",
  },
  [ApiDimension.DIMENSION_INTERNAL_CAMPAIGN_TYPE]: {
    name: "Personsalisation Campaign Type",
    description: "Type of Campaign that runs a personalisation",
  },
  [ApiDimension.DIMENSION_INTERNAL_AB_CAMPAIGN]: {
    name: "A/B Campaign",
    description: "A/B Campaign that runs a personalisation",
  },
  [ApiDimension.DIMENSION_INTERNAL_CAMPAIGN_LEAD_STATUS]: {
    name: "Campaign Control / Variant",
    description: "whether lead is in control or Variant",
  },
  [ApiDimension.DIMENSION_CONTENT]: {
    name: "Content",
    description: "content that brought in a session",
  },
  [ApiDimension.DIMENSION_TERM]: {
    name: "Term",
    description: "term that brought in a session",
  },
  [ApiDimension.DIMENSION_PATH]: {
    name: "Path",
    description: "pageview display_page",
  },
  [ApiDimension.DIMENSION_PRODUCT_NAME]: {
    name: "Product Name",
    description: "name of product linked to page",
  },
  [ApiDimension.DIMENSION_PRODUCT_CATEGORY]: {
    name: "Product Category",
    description: "category of product linked to page",
  },
  [ApiDimension.DIMENSION_PRODUCT_BRAND]: {
    name: "Product Brand",
    description: "brand of product linked to page",
  },
  [ApiDimension.DIMENSION_JOB_TITLE]: {
    name: "Job Title",
    description: "title of job linked to page",
  },
  [ApiDimension.DIMENSION_JOB_CATEGORY]: {
    name: "Job Category",
    description: "category of job linked to page",
  },
  // [ApiDimension.DIMENSION_PRODUCT]: "DimensionProduct",
  // [ApiDimension.DIMENSION_JOB]: "DimensionJob",
  [ApiDimension.DIMENSION_LOCATION_PAGE_TYPE]: {
    name: "Page Location Tag",
    description: "pageview location tag",
  },
  [ApiDimension.DIMENSION_LANDING_PAGE_PATH]: {
    name: "Landing Page Path",
    description: "pageview display_page of session landing page",
  },
  [ApiDimension.DIMENSION_LANDING_PAGE_LOCATION]: {
    name: "Landing Page Location",
    description: "First Page of a visit",
  },
  [ApiDimension.DIMENSION_SESSION_LANDING_PAGE_LOCATION]: {
    name: "Session Landing Page Location",
    description:
      "location of session landing page, this is for the entire session",
  },
  [ApiDimension.DIMENSION_SESSION_LANDING_PAGE_PATH]: {
    name: "Session Landing Page Path",
    description: "path of session landing page, this is for the entire session",
  },
  [ApiDimension.DIMENSION_LANDING_PAGE_TYPE]: {
    name: "Landing Page Location Tag",
    description: "landing pageview location tag",
  },
  [ApiDimension.DIMENSION_LANDING_PAGE_LOCATION_TAGGER]: {
    name: "Landing Page Data Key",
    description: "Key used for landing page tagging",
  },
  [ApiDimension.DIMENSION_LANDING_PAGE_LOCATION_TAGGER_VALUE]: {
    name: "Landing Page Data Value",
    description: "by landing page data value captured for key for page",
  },
  [ApiDimension.DIMENSION_LOCATION_TAGGER]: {
    name: "Page Data Key",
    description: "Key used for page tagging",
  },
  [ApiDimension.DIMENSION_LOCATION_TAGGER_VALUE]: {
    name: "Page Data Value",
    description: "by page data value captured for key for page",
  },
  [ApiDimension.DIMENSION_LANDING_NEXT_PAGE_TYPE]: {
    name: "Landing Page Next Page Location Tag",
    description: "Next landing pageview location tag",
  },
  // [ApiDimension.DIMENSION_LANDING_PAGE_META_VALUE]: {
  //   originalName: "landing_page_data_value",
  //   name: "Landing Page Data Value",
  //   description: "by session landing page data value captured for that page",
  // },
  [ApiDimension.DIMENSION_LANDING_NEXT_PAGE_PATH]: {
    name: "Landing Page Next Page Path",
    description:
      "pageview display_page of session landing_next page, important, all metrics for this dimension are related to it's landing page and should be used in conjection with landing_page dimension, for example pageview count will be the number of pagesviews that started on X location and moved onto this one",
  },
  [ApiDimension.DIMENSION_LANDING_NEXT_PAGE_LOCATION]: {
    name: "Landing Page Next Page Location",
    description:
      "location of session landing_next page, important, all metrics for this dimension are related to it's landing page and should be used in conjection with landing_page dimension, for example pageview count will be the number of pagesviews that started on X location and moved onto this one",
  },
  [ApiDimension.DIMENSION_LANDING_NEXT_PAGE_LOCATION_TAGGER]: {
    name: "Landing Page Next Data Key",
    description: "Key used for landing page tagging",
  },
  [ApiDimension.DIMENSION_LANDING_NEXT_PAGE_LOCATION_TAGGER_VALUE]: {
    name: "Landing Page Next Data Value",
    description: "by landing next page data value captured for key for page",
  },
  // [ApiDimension.DIMENSION_LANDING_NEXT_PAGE_META_VALUE]: {
  //   originalName: "landing_next_page_data_value",
  //   name: "Landing Page Next Data Value",
  //   description:
  //     "by session landing_next page data value captured for that page, important, all metrics for this dimension are related to it's landing page and should be used in conjection with landing_page dimension, for example pageview count will be the number of pagesviews that started on X location and moved onto this one",
  // },
  [ApiDimension.DIMENSION_SESSION_START_BY_HOUR]: {
    name: "Session Start by Hour",
    description: "start of the hour the session happened on",
  },
  [ApiDimension.DIMENSION_SESSION_START_BY_WEEK]: {
    name: "Session Start by Week",
    description: "start of the week the session happened on",
  },
  [ApiDimension.DIMENSION_SESSION_START_BY_MONTH]: {
    name: "Session Start by Month",
    description: "start of the month the session happened on",
    format: (v) => friendlyMonth(`${v}`),
  },
  [ApiDimension.DIMENSION_FIRST_SALE_MONTH]: {
    format: (v) => friendlyMonth(`${v}`),
  },
  [ApiDimension.DIMENSION_FIRST_SALE_DATE]: {
    name: "First Order Date",
    format: (v) => friendlyDate(`${v}`),
  },
  [ApiDimension.DIMENSION_SESSION_START_BY_WEEKDAY]: {
    name: "Session Day of Week",
    description: "day session happened on",
  },
  [ApiDimension.DIMENSION_MACRO_CONVERSION]: {
    name: "Conversion Name",
    description: "name of Conversion",
  },
  [ApiDimension.DIMENSION_MICRO_CONVERSION]: {
    name: "Interaction Name",
    description: "name of Interaction",
  },
  [ApiDimension.DIMENSION_CRAWLER]: {
    name: "Crawler Name",
    description: "name of crawler that brought in a session",
  },
  [ApiDimension.DIMENSION_DAYS_TO_SECOND_VISIT]: {
    name: "Days to Second Session",
    description:
      "no. of days a lead took to their second visit (returning only)",
  },
  [ApiDimension.DIMENSION_DAYS_TO_SECOND_PURCHASE]: {
    name: "Days to Second Purchase",
    description:
      "no. of days from first to second purchase (0 means no repeat yet)",
  },
  [ApiDimension.DIMENSION_DAYS_SINCE_PRIOR_MACRO_CONVERSION]: {
    name: "Days Since Conversion",
    description:
      "This is the number of days a lead had before each macro converting session, this resest at each leads macro converting session",
  },
  [ApiDimension.DIMENSION_VISITS_SINCE_PRIOR_MACRO_CONVERSION]: {
    name: "Sessions Since Conversion",
    description:
      "This is the number of sessions a lead had before each macro converting session, this resest at each leads macro converting session",
  },
  [ApiDimension.DIMENSION_DAYS_TO_FIRST_MACRO_CONVERSION]: {
    name: "Days To First Conversion",
    description:
      "This is the number of days a lead had had before their first conversion",
  },
  [ApiDimension.DIMENSION_VISITS_TO_FIRST_MACRO_CONVERSION]: {
    name: "Sessions To First Conversion",
    description:
      "This is the number of sessions a lead had had before their first conversion",
  },
  [ApiDimension.DIMENSION_CONVERTING_SESSION_JOURNEY]: {
    name: "Converted User Pageview Journey",
    description:
      "this is the locations journey per all sessions leading up to a conversion",
  },
  [ApiDimension.DIMENSION_CHANNEL_MACRO_CONVERSION_JOURNEY]: {
    name: "Converted User Channel Journey",
    description:
      "this is the channel journey to each conversion, resetting at each conversion, (note only applies to sessions with conversions)",
  },
  [ApiDimension.DIMENSION_SALES_SESSION_JOURNEY]: {
    name: "Sales User Pageview Journey",
    description:
      "this is the locations journey per all sessions leading up to a sale",
  },
  [ApiDimension.DIMENSION_CHANNEL_SALES_JOURNEY]: {
    name: "Sales User Channel Journey",
    description:
      "this is the channel journey to each sale, resetting at each sale, (note only applies to sessions with sales)",
  },
  [ApiDimension.DIMENSION_DOMAIN]: {
    name: "Domain",
    description: "The sites domain",
  },
  [ApiDimension.DIMENSION_IS_RETURNING_CUSTOMER]: {
    name: "Is Returning Customer",
    description: "",
  },
  [ApiDimension.DIMENSION_SESSION_START_DATE]: {
    name: "Visit Date",
    description: "",
    format: (v) => friendlyDateTime(`${v}`),
  },
  [ApiDimension.DIMENSION_SESSION_ID]: {
    name: "Visit Id",
    description: "",
  },
  [ApiDimension.DIMENSION_TRANSACTION_ID]: {
    name: "Transaction Id (order)",
    description: "",
  },
  [ApiDimension.DIMENSION_TRANSACTION_ID]: {
    name: "Transaction Id (order)",
    description: "",
  },
  [ApiDimension.DIMENSION_QUERY_STRING_PARAM]: {
    name: "Url Query String Param",
    description: "",
  },
  [ApiDimension.DIMENSION_QUERY_STRING_VALUE]: {
    name: "Url Query String Value",
    description: "",
  },
  [ApiDimension.DIMENSION_QUERY_STRING_PAIR]: {
    name: "Url Query String Key Value",
    description: "",
  },
  [ApiDimension.DIMENSION_QUERY_STRING]: {
    name: "Url Query String",
    description: "",
  },
  [ApiDimension.DIMENSION_FULL_URL]: {
    name: "Full URL",
    description: "",
  },
  [ApiDimension.DIMENSION_LANDING_PAGE_QUERY_STRING_PARAM]: {
    name: "Landing Page Query String Param",
    description: "",
  },
  [ApiDimension.DIMENSION_LANDING_PAGE_QUERY_STRING_VALUE]: {
    name: "Landing Page  Query String Value",
    description: "",
  },
  [ApiDimension.DIMENSION_LANDING_PAGE_QUERY_STRING_PAIR]: {
    name: "Landing Page  Query String Key Value",
    description: "",
  },
  [ApiDimension.DIMENSION_LANDING_PAGE_QUERY_STRING]: {
    name: "Landing Page  Query String",
    description: "",
  },
  [ApiDimension.DIMENSION_LANDING_PAGE_FULL_URL]: {
    name: "Full Landing Page URL",
    description: "",
  },
  [ApiDimension.DIMENSION_REFERRER]: {
    name: "Referrer",
    description: "",
  },
  [ApiDimension.DIMENSION_REFERRER_DOMAIN]: {
    name: "Referring Domain",
    description: "",
  },
  [ApiDimension.DIMENSION_REFERRER_PATH]: {
    name: "Referring Path",
    description: "",
  },
};

export const formatDimension = (dimension: ApiDimension, value: string) => {
  const m = dimensionMap[dimension];
  return m && m.format ? m.format(value) : value;
};

export const dimensionNameToEnum = (name: string) => {
  const keys = Object.keys(dimensionMap) as ApiDimension[];
  const foundKey = keys.find((key) => dimensionMap[key]?.name === name);
  return foundKey || name;
};

export const dimensionConfigByName = (dimension: ApiDimension) =>
  dimensionMap[dimension];
